'use client';

import { Fragment, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { SpotlightGallery } from './SpotlightGallery';
import {
  IconCase,
  IconCore,
  IconCoverage,
  IconCycle,
  IconHeartRate,
  IconJade,
  IconMax,
  IconPowerPlugs,
  IconRing,
} from './spotlightIcons';
import styles from './RingProUsSpotlight.module.css';

type RowMark = () => ReactNode;

const INCLUDED: { key: string; icon: RowMark }[] = [
  { key: 'ring', icon: IconRing },
  { key: 'case', icon: IconCase },
  { key: 'core', icon: IconCore },
  { key: 'cycle', icon: IconCycle },
  { key: 'jade', icon: IconJade },
  { key: 'service', icon: IconHeartRate },
];

const OPTIONAL: { key: string; icon: RowMark }[] = [
  { key: 'max', icon: IconMax },
  { key: 'powerPlugs', icon: IconPowerPlugs },
  { key: 'coverage', icon: IconCoverage },
];

const BENEFITS = ['sizingKit', 'returns', 'warranty'] as const;

type Props = {
  /** Formatted base price, e.g. "$479". */
  price: string;
};

/** Centers a glyph in the 24px row slot or the 20px trust slot. */
const IconSlot = ({
  children,
  size,
}: {
  children: ReactNode;
  size: 20 | 24;
}) => (
  <span className={size === 24 ? styles.icon24 : styles.icon20} aria-hidden>
    {children}
  </span>
);

/**
 * "Ring PRO / More power to you" for the US homepage (Figma 2535:10731):
 * sticky gallery on the left; on the right, What's included (price,
 * checklist, Buy), Optional add-ons, and the service benefits.
 */
export const RingProUsSpotlight = ({ price }: Props) => {
  const t = useTranslations('homepage');
  const k = (key: string) => `homepage.ringProUs.spotlight.${key}`;

  return (
    <section
      className={styles.section}
      aria-labelledby="ring-pro-us-spotlight-title"
    >
      <div className={styles.inner}>
        {/* Hidden for now (design review, Oct 26); still names the section
            for screen readers. Drop `srOnly` to bring it back. */}
        <h2
          id="ring-pro-us-spotlight-title"
          className={`${styles.title} ${styles.srOnly}`}
        >
          <span>{t(k('title'))}</span>
          <span className={styles.titleFaint}>{t(k('tagline'))}</span>
        </h2>

        <div className={styles.grid}>
          <div className={styles.left}>
            <SpotlightGallery />
          </div>

          <div className={styles.right}>
            {/* What's included */}
            <article className={`${styles.card} ${styles.cardIncluded}`}>
              <header className={styles.cardHead}>
                <p className={styles.eyebrow}>{t(k('included.eyebrow'))}</p>
                <p className={styles.price}>
                  <span className={styles.priceAmount}>{price}</span>{' '}
                  <span className={styles.priceNote}>
                    {t(k('included.priceNote'))}
                  </span>
                </p>
                <p className={styles.sub}>{t(k('included.sub'))}</p>
              </header>

              <ul className={styles.list}>
                {INCLUDED.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <Fragment key={item.key}>
                      {i > 0 ? (
                        <li
                          className={styles.rule}
                          aria-hidden
                          role="presentation"
                        />
                      ) : null}
                      <li className={styles.row}>
                        <IconSlot size={24}>
                          <Icon />
                        </IconSlot>
                        <span className={styles.rowText}>
                          {t(k(`included.items.${item.key}`))}
                        </span>
                      </li>
                    </Fragment>
                  );
                })}
              </ul>

              <CustomLink
                href="/ring-pro/buy"
                className={styles.buy}
                data-buttontype="ring_pro_us_spotlight_buy_button"
              >
                {t(k('included.cta'))}
              </CustomLink>
            </article>

            {/* Optional */}
            <article className={`${styles.card} ${styles.cardOptional}`}>
              <header className={styles.cardHead}>
                <p className={styles.eyebrow}>{t(k('optional.eyebrow'))}</p>
                <p className={styles.optTitle}>{t(k('optional.title'))}</p>
              </header>

              <ul className={styles.list}>
                {OPTIONAL.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <Fragment key={item.key}>
                      {i > 0 ? (
                        <li
                          className={styles.rule}
                          aria-hidden
                          role="presentation"
                        />
                      ) : null}
                      <li className={styles.row}>
                        <IconSlot size={24}>
                          <Icon />
                        </IconSlot>
                        <span className={styles.optCopy}>
                          <span className={styles.optHead}>
                            <span className={styles.rowText}>
                              {t(k(`optional.${item.key}.name`))}
                            </span>
                            <span className={styles.optPrice}>
                              {t(k(`optional.${item.key}.price`))}
                            </span>
                          </span>
                          <span className={styles.optBody}>
                            {t(k(`optional.${item.key}.body`))}
                          </span>
                        </span>
                      </li>
                    </Fragment>
                  );
                })}
              </ul>
            </article>

            {/* Service benefits */}
            <ul className={styles.benefits}>
              {BENEFITS.map((key, i) => (
                <Fragment key={key}>
                  {i > 0 ? (
                    <li
                      className={styles.benefitRule}
                      aria-hidden
                      role="presentation"
                    />
                  ) : null}
                  <li className={styles.benefit}>
                    <span className={styles.benefitTitle}>
                      {t(k(`benefits.${key}.title`))}
                    </span>
                    <span className={styles.benefitSub}>
                      {t(k(`benefits.${key}.sub`))}
                    </span>
                  </li>
                </Fragment>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RingProUsSpotlight;
